import {
    Box,
    Dialog,
    IconButton,
    Modal,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { useSlate } from 'slate-react';
import {
    renderHtml,
    // renderReactEmail,
    // renderReactEmailAsJson,
} from '../Utils/serializeEmail';
import { useEffect, useState } from 'react';
import { CloseRounded, ContentCopyRounded, Height } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { isDev } from '@Utils/dev';
import useEditorProps from '@hooks/useEditorProps';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { template, templateSettings } from 'lodash';

function HtmlCode({}) {
    const editor = useSlate();
    const { mergeTags } = useEditorProps();
    const [renderedHtml, setRenderedHtml] = useState('');

    const getHtml = async () => {
        const html = await renderHtml(editor, mergeTags, { pretty: true });
        setRenderedHtml(html);
    };

    getHtml();

    if (!renderedHtml) return <h1>Loading...</h1>;

    return (
        <div
            dir="ltr"
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                overflow: 'auto',
            }}
        >
            <IconButton
                onClick={() => navigator.clipboard.writeText(renderedHtml)}
                sx={{ position: 'absolute', right: 0, top: 20 }}
                color="primary"
            >
                <ContentCopyRounded />
            </IconButton>
            <SyntaxHighlighter showLineNumbers language="html" style={dracula}>
                {renderedHtml}
            </SyntaxHighlighter>
        </div>
    );
}
// const decoder = new TextDecoder('utf-8');

// const readStream = async stream => {
//     const chunks = [];

//     if ('pipeTo' in stream) {
//         // means it's a readable stream
//         const writableStream = new WritableStream({
//             write(chunk: Uint8Array) {
//                 chunks.push(chunk);
//             },
//         });
//         await stream.pipeTo(writableStream);
//     } else {
//         throw new Error(
//             'For some reason, the Node version of `react-dom/server` has been imported instead of the browser one.',
//             {
//                 cause: {
//                     stream,
//                 },
//             },
//         );
//     }

//     let length = 0;
//     chunks.forEach(item => {
//         length += item.length;
//     });
//     const mergedChunks = new Uint8Array(length);
//     let offset = 0;
//     chunks.forEach(item => {
//         mergedChunks.set(item, offset);
//         offset += item.length;
//     });

//     return decoder.decode(mergedChunks);
// };

// const reactDOMServer = await import('react-dom/server');
// const renderToReadableStream = await reactDOMServer.renderToReadableStream(
//     <h1>renderToReadableStream</h1>,
// );
// console.log(await readStream(renderToReadableStream));

const DesktopPreview = ({}) => {
    const editor = useSlate();
    const { mergeTags } = useEditorProps();
    const [renderedHtml, setRenderedHtml] = useState('');

    const getHtml = async () => {
        const html = await renderHtml(editor, mergeTags, {
            replaceMergeTags: true,
        });
        setRenderedHtml(html);
    };

    getHtml();

    if (!renderedHtml) return <h1>Loading...</h1>;

    return (
        <iframe
            srcDoc={renderedHtml}
            style={{
                width: '100%',
                height: '100%',
                border: 'none',
            }}
        />
    );
};

const MobilePreview = ({}) => {
    const editor = useSlate();
    const { mergeTags } = useEditorProps();
    const [renderedHtml, setRenderedHtml] = useState('');

    const getHtml = async () => {
        const html = await renderHtml(editor, mergeTags, {
            replaceMergeTags: true,
        });
        setRenderedHtml(html);
    };

    getHtml();

    if (!renderedHtml) return <h1>Loading...</h1>;
    return (
        <iframe
            srcDoc={renderedHtml}
            style={{
                width: '375px',
                height: '100%',
                border: 'none',
            }}
        />
    );
};

const SlatePreview = ({}) => {
    const editor = useSlate();
    const data = JSON.stringify(editor.children, null, 2);
    return (
        <div
            dir="ltr"
            style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                overflow: 'auto',
            }}
        >
            <IconButton
                onClick={() => navigator.clipboard.writeText(data)}
                sx={{ position: 'absolute', right: 0, top: 20 }}
                color="primary"
            >
                <ContentCopyRounded />
            </IconButton>
            <SyntaxHighlighter showLineNumbers language="json" style={dracula}>
                {data}
            </SyntaxHighlighter>
        </div>
    );
};

const previewMap = {
    desktop: DesktopPreview,
    mobile: MobilePreview,
    html: HtmlCode,
    slate: SlatePreview,
};

function Preview({ open, onClose }) {
    const { t } = useTranslation();
    const editor = useSlate();
    const [previewMode, setPreviewMode] = useState('desktop');

    const PreviewComponent = previewMap[previewMode];

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'white',
                }}
            >
                <Box
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        padding: 2,
                        justifyContent: 'space-between',
                    }}
                >
                    <Box width={48}>
                        <IconButton onClick={onClose}>
                            <CloseRounded fontSize="large" />
                        </IconButton>
                    </Box>
                    <Box>
                        <ToggleButtonGroup
                            color="primary"
                            value={previewMode}
                            exclusive
                            onChange={(e, v) => v && setPreviewMode(v)}
                        >
                            <ToggleButton
                                value="desktop"
                                sx={{ width: 96, py: 0.5 }}
                            >
                                {t('Desktop')}
                            </ToggleButton>
                            <ToggleButton
                                value="mobile"
                                sx={{ width: 96, py: 0.5 }}
                            >
                                {t('Mobile')}
                            </ToggleButton>
                            {isDev && [
                                <ToggleButton key="1" value="html">
                                    HTML
                                </ToggleButton>,
                                <ToggleButton key="2" value="slate">
                                    Slate
                                </ToggleButton>,
                            ]}
                        </ToggleButtonGroup>
                    </Box>
                    <Box width={48} />
                </Box>
                <Box
                    width="100%"
                    flex={1}
                    overflow="auto"
                    display="flex"
                    justifyContent="center"
                >
                    <PreviewComponent />
                </Box>
            </Box>
        </Modal>
    );

    // return (
    //     <Dialog fullWidth open={open} onClose={onClose}>
    //         <div>
    //             <button onClick={() => setPreviewMode('slate')}>Slate</button>
    //             <button onClick={() => setPreviewMode('html')}>HTML</button>
    //             <button onClick={() => setPreviewMode('react')}>React</button>
    //             <button onClick={() => setPreviewMode('code')}>Code</button>
    //             <button
    //                 onClick={() => {
    //                     setPreviewMode('jsx');
    //                     console.log(renderReactEmailAsJson(editor));
    //                 }}
    //             >
    //                 JSX
    //             </button>
    //         </div>
    //         {previewMode === 'slate' && (
    //             <pre>{JSON.stringify(editor.children, null, 2)}</pre>
    //         )}
    //         {previewMode === 'html' && (
    //             <iframe
    //                 title="Preview"
    //                 srcDoc={renderHtml(editor)}
    //                 style={{ width: '99%', height: '700px' }}
    //             />
    //         )}
    //         {previewMode === 'react' && renderReactEmail(editor)}
    //         {previewMode === 'code' && <pre>{renderHtml(editor)}</pre>}
    //         {previewMode === 'jsx' && (
    //             <pre>{renderReactEmailAsJson(editor)}</pre>
    //         )}
    //     </Dialog>
    // );
}

export default Preview;
